import { useEffect, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import ErrorModal from "./components/ErrorModal";
import History from "./helpers/history";
import { Router } from "./routes/Router";
import Maintenance from "./components/Maintenance";
import { MAINTENANCE_MODE } from "./Config";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useCustomNavigate } from "./hooks/useCustomNavigate";
import IncomingCall from "components/IncomingCall/IncomingCall";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "components/ui/sonner";
import { TooltipProvider } from "components/ui/tooltip";
import { formatError } from "helpers/formatError";
import { ApplyTagDialogProvider } from "providers/ApplyTagDialogProvider";
import { RuvixxFlagsProvider } from "providers/RuvixxFlagsProvider";
import { AnalyticsProvider } from "providers/AnalyticsProvider";

const queryClient = new QueryClient({
  // Opt-in error surface: a query that sets `meta.errorMessage` is reported
  // as a toast; queries without it keep handling errors themselves.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.meta?.errorMessage) {
        toast.error(formatError(error, query.meta.errorMessage));
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const SetHistoryInstance = () => {
  const navigate = useCustomNavigate();

  useEffect(() => {
    History.historyInstance = navigate;
  }, [navigate]);

  return null;
};

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(MAINTENANCE_MODE);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      // Listen for updates from the service worker
      navigator.serviceWorker.addEventListener("message", event => {
        if (event.data && event.data.type === "UPDATE_AVAILABLE") {
          setMaintenanceMode(event.data.maintenanceMode);
        }
      });
    }
  }, []);

  if (maintenanceMode) {
    return (
      <div className="tw:h-screen tw:bg-background">
        <Maintenance />
      </div>
    );
  } else {
    return (
      <BrowserRouter>
        <AnalyticsProvider>
          <NuqsAdapter>
            <SetHistoryInstance />
            <QueryClientProvider client={queryClient}>
              <RuvixxFlagsProvider>
                <TooltipProvider>
                  <ApplyTagDialogProvider>
                    <IncomingCall />
                    <Toaster />
                    <ErrorModal>
                      <Router />
                      <ReactQueryDevtools initialIsOpen={false} />
                    </ErrorModal>
                  </ApplyTagDialogProvider>
                </TooltipProvider>
              </RuvixxFlagsProvider>
            </QueryClientProvider>
          </NuqsAdapter>
        </AnalyticsProvider>
      </BrowserRouter>
    );
  }
}

export default App;
export { queryClient };
